import CryptoJS from 'crypto-js';
import sha256 from 'crypto-js/sha256';
import { EncryptStorage } from 'encrypt-storage';

import { OrderStatus, PaymentStatus } from 'hooks/helpers';
import { Customer } from 'model/Customer';
import { Payment, Waypoint } from 'model/Order';
import { QuoteRequest, QuoteResponse, QuoteFormValues } from 'model/Quote';
import { Vehicle } from 'model/Vehicle';

import { PaymentIntentMutationResult, StripeCustomer } from './bff';
import { events } from './events';

type Session = {
    quoteForm: Partial<QuoteFormValues>,
    quoteRequest: Partial<QuoteRequest>,
    quoteResponse: Partial<QuoteResponse>,
    quoteAccepted: boolean;
    enclosed: boolean;
    customer: Partial<Customer>;
    vehicles: Partial<Vehicle>[];
    pickup: Partial<Waypoint>;
    delivery: Partial<Waypoint>;
    paymentIntent: Partial<PaymentIntentMutationResult>;
    paymentStatus: PaymentStatus;
    orderStatus: OrderStatus;
    orderId: string;
    payment: Payment;
    gclid: string;
    sdk: string;
};

const createSessionClient = () => {
  // @ts-ignore
  const countUpTo = (limit: number) => [...Array(limit).keys()];

  const secret = sha256("shhh... don't tell anyone, this is supposed to be a secret")
    .toString(CryptoJS.enc.Base64);

  const encryptedSession = new EncryptStorage(secret, {
    storageType: 'sessionStorage',
    stateManagementUse: true,
  });

  const getSessionValue = <K extends keyof Session, V = Session[K], X = V>
    (key: K, fallback?: V, xfmr?: (value: X) => V) => {
    const value = encryptedSession.getItem(key);
    if (!value && !fallback) {
      return null;
    }
    if (!value) {
      return fallback;
    }
    return xfmr ? xfmr(JSON.parse(value)) : JSON.parse(value) as V;
  };

  const setSessionValue = <K extends keyof Session, V = Session[K], X = V>
    (key: K, value: V, xfmr?: (value: V) => X) => {
    return encryptedSession.setItem(key, JSON.stringify(xfmr ? xfmr(value) : value));
  };

  const getQuoteForm = () => getSessionValue('quoteForm', {}) as Partial<QuoteFormValues>;
  const setQuoteForm = (quoteForm: Partial<QuoteFormValues>) => setSessionValue('quoteForm', quoteForm);

  const getQuoteRequest = () => getSessionValue('quoteRequest', {}) as Partial<QuoteRequest>;
  const setQuoteRequest = (quoteRequest: Partial<QuoteRequest>) => setSessionValue('quoteRequest', quoteRequest);

  const getQuoteResponse = () => getSessionValue('quoteResponse', {}) as Partial<QuoteResponse>;
  const setQuoteResponse = (quoteResponse: Partial<QuoteResponse>) => setSessionValue('quoteResponse', quoteResponse);

  const getQuoteAccepted = () => getSessionValue('quoteAccepted', false) as boolean;
  const setQuoteAccepted = (quoteAccepted: boolean) => setSessionValue('quoteAccepted', quoteAccepted);

  const getCustomer = () => getSessionValue('customer', {}) as Partial<StripeCustomer>;
  const setCustomer = (customer: Partial<StripeCustomer>) => setSessionValue('customer', customer);

  const getEnclosed = () => getSessionValue('enclosed', false) as boolean;
  const setEnclosed = (enclosed: boolean) => setSessionValue('enclosed', enclosed);

  const getVehicles = () => {
    const vehicles = getSessionValue('vehicles', []) as Partial<Vehicle>[];
    if (vehicles.length === 0) {
      countUpTo(getQuoteForm().numberOfVehicles as number)
        .forEach(() => vehicles.push({
          vin: '',
          operational: true,
          make: '',
          model: '',
          year: 0,
        }));
      setVehicles(vehicles);
    }
    return vehicles;
  };

  const setVehicles = (vehicles: Partial<Vehicle>[]) => {
    setSessionValue('vehicles', vehicles);
    events.emitVehiclesUpdated(vehicles);
  };

    interface FlatWaypoint extends Omit<Partial<Waypoint>, 'date'> {
        date?: string;
    }

    const waypointFlattener = (waypoint: Partial<Waypoint>): FlatWaypoint => {
      const { date, ...rest } = waypoint;
      return {
        ...rest,
        date: date ? `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}` : undefined,
      };
    };

    const waypointFattener = (waypoint: FlatWaypoint): Partial<Waypoint> => {
      const { date, ...rest } = waypoint;
      const [year, month, day] = (date ?? '').split('-');
      return {
        ...rest,
        date: date ? new Date(parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10)) : undefined,
      };
    };

    const getPickup = () => getSessionValue('pickup', {}, waypointFattener) as Partial<Waypoint>;
    const setPickup = (pickup: Partial<Waypoint>) => setSessionValue('pickup', pickup, waypointFlattener);

    const getDelivery = () => getSessionValue('delivery', {}, waypointFattener) as Partial<Waypoint>;
    const setDelivery = (delivery: Partial<Waypoint>) => setSessionValue('delivery', delivery, waypointFlattener);

    const getPaymentIntent = () => getSessionValue('paymentIntent', {}) as Partial<PaymentIntentMutationResult>;
    const setPaymentIntent = (paymentIntent: Partial<PaymentIntentMutationResult>) => setSessionValue('paymentIntent', paymentIntent);

    const getPaymentStatus = () => getSessionValue('paymentStatus', PaymentStatus.NEW) as PaymentStatus;
    const setPaymentStatus = (paymentStatus: PaymentStatus) => setSessionValue('paymentStatus', paymentStatus);

    const getOrderStatus = () => getSessionValue('orderStatus', OrderStatus.NEW) as OrderStatus;
    const setOrderStatus = (orderStatus: OrderStatus) => setSessionValue('orderStatus', orderStatus);

    const getOrderId = () => getSessionValue('orderId', null);
    const setOrderId = (orderId: string) => setSessionValue('orderId', orderId);

    const getPayment = () => getSessionValue('payment', {}) as Partial<Payment>;
    const setPayment = (payment: Payment) => setSessionValue('payment', payment);

    const getGclid = () => getSessionValue('gclid', undefined) as string | undefined;
    const setGclid = (gclid: string) => setSessionValue('gclid', gclid);

    const getSdk = () => getSessionValue('sdk', null) as string | undefined;
    const setSdk = (gclid: string) => setSessionValue('sdk', gclid);

    const clearSession = () => {
      encryptedSession.clear();
    };

    const session = {
      getQuoteForm,
      setQuoteForm,
      getQuoteRequest,
      setQuoteRequest,
      getQuoteResponse,
      setQuoteResponse,
      getQuoteAccepted,
      setQuoteAccepted,
      getCustomer,
      setCustomer,
      getEnclosed,
      setEnclosed,
      getVehicles,
      setVehicles,
      getPickup,
      setPickup,
      getDelivery,
      setDelivery,
      getPaymentIntent,
      setPaymentIntent,
      getPaymentStatus,
      setPaymentStatus,
      getOrderStatus,
      setOrderStatus,
      getOrderId,
      setOrderId,
      clearSession,
      getPayment,
      setPayment,
      getGclid,
      setGclid,
      getSdk,
      setSdk,
    };

    return { session };
};

const sessionClient = createSessionClient();

export const { session } = sessionClient;
